import { useState } from 'react';
import { Link } from 'react-router-dom';
import UtilityBar from '../components/layout/UtilityBar';
import Nav from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Stat from '../components/ui/Stat';
import ImageSlot from '../components/ui/ImageSlot';
import Select from '../components/ui/Select';
import { COURSES } from '../data/courses';

const CATEGORIES = ['All', 'AI & Tech', 'Math', 'Science', 'Languages', 'Humanities', 'Vocational', 'Test Prep'];

/** Courses index — search, faceted filters, paginated grid. */
export default function CoursesIndex() {
  // Synthesize 12 cards from the 6-course dataset by duplicating
  const grid = [...COURSES, ...COURSES.slice(0, 6)];
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState('Most popular');

  return (
    <div style={{ background: 'var(--bg)' }}>
      <UtilityBar />
      <Nav variant="light" />

      <section className="relative overflow-hidden text-white" style={{ background: 'var(--navy)', padding: '80px 0 40px' }}>
        <div className="absolute pointer-events-none rounded-full"
          style={{ top: -100, right: -100, width: 500, height: 500, background: 'radial-gradient(circle, rgba(240,184,12,0.18), transparent 65%)' }} />
        <div className="lc-container on-navy relative">
          <div className="flex justify-between items-end">
            <div>
              <span className="lc-eyebrow on-dark">All courses</span>
              <h1 className="text-white mt-3.5" style={{ fontSize: 64 }}>
                <span style={{ color: 'var(--yellow)' }}>15+</span> courses,<br />taught by Lincoln.
              </h1>
              <p className="mt-4" style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)', maxWidth: 560 }}>
                Curated by 4 providers — universities, ministries, tutoring agencies, and certified educators. All voice-first. All multilingual.
              </p>
            </div>
            <div className="flex" style={{ gap: 16 }}>
              <Stat value="15+" label="Courses" />
              <Stat value="4" label="Providers" color="#7be3b0" />
              <Stat value="3" label="Languages" />
            </div>
          </div>

          <div className="bg-white flex items-center gap-2 mt-12"
            style={{ borderRadius: 16, padding: 8, maxWidth: 880, boxShadow: 'var(--shadow-lg)', minWidth: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.4" style={{ marginLeft: 8, flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              placeholder="Search topics…"
              className="flex-1 outline-none"
              style={{ border: 0, fontSize: 15, padding: '10px 0', color: 'var(--ink)', fontFamily: 'inherit', minWidth: 0, width: '100%' }}
            />
            <button type="button" className="hidden md:inline-flex items-center justify-center flex-shrink-0"
              style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--bg-soft)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.4">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
              </svg>
            </button>
            <button type="button" className="lc-btn primary sm flex-shrink-0">Search</button>
          </div>
          <div className="mt-3.5 flex items-center gap-3" style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
            <span>Or just speak: <em style={{ color: 'var(--yellow-2)' }}>&ldquo;Lincoln, find me a free AI course in Finnish&rdquo;</em></span>
          </div>

          <div className="flex flex-wrap gap-2" style={{ marginTop: 36 }}>
            {CATEGORIES.map((c, i) => (
              <button key={c} type="button" className="rounded-full font-semibold"
                style={{
                  padding: '10px 18px',
                  fontSize: 14,
                  background: i === 0 ? 'var(--yellow)' : 'rgba(255,255,255,0.08)',
                  color: i === 0 ? 'var(--navy)' : '#fff',
                  border: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.15)',
                }}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="lc-section">
        <div className="lc-container">
          <div className="grid" style={{ gridTemplateColumns: '260px 1fr', gap: 40 }}>
            <aside className="self-start md:sticky" style={{ top: 90 }}>
              {/* Mobile-only toggle */}
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                className="md:hidden w-full flex items-center justify-between font-bold"
                style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid var(--line)', background: 'var(--bg-soft)', fontSize: 14, color: 'var(--ink)', marginBottom: 12 }}
                aria-expanded={filtersOpen}
              >
                <span className="inline-flex items-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M3 6h18M6 12h12M10 18h4" /></svg>
                  Filters
                </span>
                <span style={{ fontSize: 12 }}>{filtersOpen ? '▲' : '▼'}</span>
              </button>

              <div className={`${filtersOpen ? 'block' : 'hidden'} md:block`}>
                {[
                  { title: 'Level',       opts: ['Beginner (84)', 'Intermediate (96)', 'Advanced (60)'] },
                  { title: 'Language',    opts: ['English (240)', 'Finnish (78)', 'Swedish (45)', 'Maltese (32)', 'Spanish (54)', '+ 35 more'] },
                  { title: 'Duration',    opts: ['< 1 week', '1–4 weeks', '1–3 months', 'Self-paced'] },
                  { title: 'Provider',    opts: ['Lincoln School', 'WEF Partner', 'Ministry-issued', 'University-partnered', 'Independent tutor'] },
                  { title: 'Price',       opts: ['Free (180)', 'Paid (60)', 'Scholarship eligible'] },
                  { title: 'Certificate', opts: ['Issues certificate', 'Issues digital badge', 'No certificate'] },
                ].map((g) => (
                  <div key={g.title} className="mb-6 pb-6" style={{ borderBottom: '1px solid var(--line)' }}>
                    <div className="font-bold mb-2.5" style={{ fontSize: 13, color: 'var(--ink)' }}>{g.title}</div>
                    <div className="flex flex-col gap-2">
                      {g.opts.map((o) => (
                        <label key={o} className="flex items-center gap-2 cursor-pointer" style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                          <input type="checkbox" style={{ accentColor: 'var(--green)' }} />
                          {o}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </aside>

            <div>
              <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <div style={{ fontSize: 14, color: 'var(--muted)' }}>
                  Showing <strong style={{ color: 'var(--ink)' }}>1–12</strong> of <strong style={{ color: 'var(--ink)' }}>15 courses</strong>
                </div>
                <div className="flex items-center gap-2" style={{ fontSize: 13 }}>
                  <span style={{ color: 'var(--muted)' }}>Sort by:</span>
                  <Select
                    value={sort}
                    onChange={setSort}
                    options={['Most popular', 'Newest', 'Highest rated', 'Shortest']}
                  />
                </div>
              </div>

              <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
                {grid.map((c, i) => (
                  <CourseCardSmall key={`${c.slug}-${i}`} {...c} featured={i === 0} />
                ))}
              </div>

              <div className="flex justify-center items-center mt-12 gap-1.5">
                {[1, 2, 3, 4, 5, '…', 20].map((p, i) => (
                  <button key={i} type="button" className="font-semibold"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: p === 1 ? 'var(--navy)' : 'transparent',
                      color: p === 1 ? '#fff' : 'var(--ink)',
                      border: p === 1 ? 'none' : '1px solid var(--line)',
                    }}>{p}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CourseCardSmall({ slug, title, provider, level, duration, learners, color, icon, featured, img }) {
  return (
    <Link to={`/courses/${slug}`} className="lc-card overflow-hidden block">
      <div className="relative overflow-hidden" style={{ height: 130, background: color }}>
        <ImageSlot id={`courses-idx-${slug}`} style={{ position: 'absolute', inset: 0 }} shape="rect" src={img} placeholder={`${title} thumbnail`} />
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.4) 100%)' }} />
        <span className="absolute font-bold tracking-wider text-white pointer-events-none"
          style={{ top: 10, left: 10, background: 'rgba(11,31,56,0.92)', padding: '3px 8px', borderRadius: 4, fontSize: 9 }}>{level.toUpperCase()}</span>
        <span className="absolute font-bold pointer-events-none"
          style={{ top: 10, right: 10, background: 'var(--yellow)', color: 'var(--navy)', padding: '3px 8px', borderRadius: 4, fontSize: 11 }}>{icon}</span>
        {featured && (
          <span className="absolute font-bold pointer-events-none"
            style={{ bottom: 10, left: 10, background: 'var(--yellow)', color: 'var(--navy)', padding: '3px 8px', borderRadius: 4, fontSize: 9 }}>★ FEATURED</span>
        )}
      </div>
      <div style={{ padding: 16 }}>
        <div className="font-bold uppercase tracking-wider" style={{ fontSize: 10, color: 'var(--muted)' }}>{provider}</div>
        <h4 className="mt-1.5" style={{ fontSize: 17 }}>{title}</h4>
        <div className="flex items-center mt-3" style={{ gap: 12, fontSize: 11, color: 'var(--muted)' }}>
          <span>⏱ {duration}</span>
          <span>👥 {learners}</span>
          <span className="ml-auto font-bold" style={{ color: 'var(--green-2)', fontSize: 12 }}>Free</span>
        </div>
      </div>
    </Link>
  );
}
